import { useEffect, useState } from 'react'
import { Check, Pencil, Users, Target, Flame, Swords, AlertTriangle, TrendingDown, Sparkles, ShieldCheck, Zap } from 'lucide-react'
import type { FitThesis, FitThesisBlockKey } from '@ispot/shared'
import type { PersonaSummary } from './FitThesisMatrix'

export type LabelResolver = (kind: 'competitor' | 'flaw' | 'capability', id: string | null | undefined) => string | null

interface Props {
  thesis: FitThesis
  persona: PersonaSummary | null
  lastSavedBlock: FitThesisBlockKey | null
  resolveLabel: LabelResolver
  onDirectEdit(blockKey: FitThesisBlockKey, value: unknown): Promise<void>
}

interface BlockSpec {
  key: FitThesisBlockKey
  label: string
  icon: typeof Users
  /** How to render the current value for display. Returns null if empty. */
  display(thesis: FitThesis, persona: PersonaSummary | null, resolve: LabelResolver): string | null
  /** Whether this block is user-editable inline (text-based blocks) */
  directEdit?: 'text' | 'textarea' | null
}

/**
 * When an ID is stored but the human label hasn't loaded yet (or the referenced
 * entity has been deleted), show a saved-but-unresolved placeholder instead of
 * the raw UUID. The block still counts as filled.
 */
const SAVED_PLACEHOLDER = 'Saved. Label unavailable.'

const BLOCKS: BlockSpec[] = [
  {
    key: 'persona',
    label: '1 Persona',
    icon: Users,
    display: (_, p) => p?.name ?? null,
  },
  {
    key: 'jtbd',
    label: '2 JTBD',
    icon: Target,
    display: (t, p) => p?.jtbds.find(j => j.id === t.jtbdId)?.label ?? (t.jtbdId ? SAVED_PLACEHOLDER : null),
  },
  {
    key: 'pain',
    label: '3 Pain',
    icon: Flame,
    display: (t, p) => {
      if (!t.painId) return null
      if (!p) return SAVED_PLACEHOLDER
      const active = p.jtbds.find(j => j.id === t.jtbdId)
      const fromActive = (active as unknown as { pains?: Array<{ id: string; label: string }> })?.pains?.find(pp => pp.id === t.painId)
      if (fromActive) return fromActive.label
      for (const j of p.jtbds) {
        const hit = (j as unknown as { pains?: Array<{ id: string; label: string }> }).pains?.find(pp => pp.id === t.painId)
        if (hit) return hit.label
      }
      return SAVED_PLACEHOLDER
    },
  },
  {
    key: 'competitor',
    label: '4 Competitor',
    icon: Swords,
    display: (t, _, resolve) => t.competitorId ? (resolve('competitor', t.competitorId) ?? SAVED_PLACEHOLDER) : null,
  },
  {
    key: 'flaw',
    label: '5 Competitor flaw',
    icon: AlertTriangle,
    display: (t, _, resolve) => t.competitorFlawId ? (resolve('flaw', t.competitorFlawId) ?? SAVED_PLACEHOLDER) : null,
  },
  {
    key: 'consequence',
    label: '6 Consequence',
    icon: TrendingDown,
    display: t => t.consequenceText,
    directEdit: 'textarea',
  },
  {
    key: 'pitch',
    label: '7 Solution pitch',
    icon: Sparkles,
    display: t => t.solutionPitch,
    directEdit: 'textarea',
  },
  {
    key: 'benefitA',
    label: '8 Benefit A (fixes pain)',
    icon: ShieldCheck,
    display: (t, _, resolve) => t.benefitACapabilityId ? (resolve('capability', t.benefitACapabilityId) ?? SAVED_PLACEHOLDER) : null,
  },
  {
    key: 'benefitB',
    label: '9 Benefit B (beats flaw)',
    icon: Zap,
    display: (t, _, resolve) => t.benefitBCapabilityId ? (resolve('capability', t.benefitBCapabilityId) ?? SAVED_PLACEHOLDER) : null,
  },
]

export function FitThesisBlockPanel({ thesis, persona, lastSavedBlock, resolveLabel, onDirectEdit }: Props) {
  const [flashKey, setFlashKey] = useState<FitThesisBlockKey | null>(null)
  useEffect(() => {
    if (!lastSavedBlock) return
    setFlashKey(lastSavedBlock)
    const t = setTimeout(() => setFlashKey(null), 1200)
    return () => clearTimeout(t)
  }, [lastSavedBlock])

  const [editing, setEditing] = useState<FitThesisBlockKey | null>(null)

  return (
    <aside className="flex min-h-0 flex-col bg-bg max-[900px]:hidden">
      <div className="flex items-center justify-between border-b border-border px-6 py-3.5 text-caption font-semibold uppercase tracking-wider text-fg-muted">
        <span>9-block thesis</span>
        <span className="lowercase tracking-normal text-fg-secondary">{thesis.blockCount}/9 filled</span>
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto">
        {BLOCKS.map(spec => {
          const value = spec.display(thesis, persona, resolveLabel)
          const filled = Boolean(value)
          const flashing = flashKey === spec.key
          const isEditing = editing === spec.key
          const Icon = spec.icon
          return (
            <div
              key={spec.key}
              className="flex flex-col gap-1.5 border-b border-border-subtle px-5 py-3 transition-colors duration-200"
              style={flashing
                ? { background: 'color-mix(in oklab, var(--color-accent) 8%, var(--color-bg-card))' }
                : { background: 'var(--color-bg)' }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <BlockNum filled={filled} />
                  <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${filled ? 'text-accent-500' : 'text-fg-muted'}`} />
                  <span className={`truncate text-caption font-semibold uppercase tracking-wide ${filled ? 'text-fg' : 'text-fg-muted'}`}>
                    {spec.label}
                  </span>
                </div>
                {spec.directEdit && (
                  <button
                    onClick={() => setEditing(isEditing ? null : spec.key)}
                    className="flex-shrink-0 rounded p-1 text-fg-muted hover:bg-bg-secondary hover:text-fg"
                    title="Edit directly"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </div>

              {!isEditing && (
                <div
                  className={`text-caption leading-snug ${filled ? 'text-fg' : 'italic text-fg-muted'}`}
                  style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                  {value ?? 'Not yet filled. Chat with Skerpa.'}
                </div>
              )}

              {isEditing && spec.directEdit && (
                <DirectEditor
                  initialValue={String(value ?? '')}
                  multiline={spec.directEdit === 'textarea'}
                  onCancel={() => setEditing(null)}
                  onSave={async val => {
                    await onDirectEdit(spec.key, val)
                    setEditing(null)
                  }}
                />
              )}
            </div>
          )
        })}
      </div>
    </aside>
  )
}

function BlockNum({ filled }: { filled: boolean }) {
  return (
    <span
      className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
      style={filled
        ? { background: 'var(--color-accent)', color: 'var(--color-bg-card)' }
        : { background: 'var(--color-border)', color: 'var(--color-fg-muted)' }}
    >
      {filled ? <Check className="h-3 w-3" /> : '•'}
    </span>
  )
}

function DirectEditor({ initialValue, multiline, onCancel, onSave }: {
  initialValue: string
  multiline: boolean
  onCancel(): void
  onSave(value: string): Promise<void>
}) {
  const [value, setValue] = useState(initialValue)
  const [saving, setSaving] = useState(false)
  async function handleSave() {
    setSaving(true)
    try { await onSave(value.trim()) } finally { setSaving(false) }
  }
  return (
    <div className="flex flex-col gap-2 pt-1">
      {multiline ? (
        <textarea
          autoFocus
          value={value}
          onChange={e => setValue(e.target.value)}
          rows={3}
          className="resize-none rounded-md border border-border bg-bg-card px-3 py-2 text-body text-fg outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
        />
      ) : (
        <input
          autoFocus
          value={value}
          onChange={e => setValue(e.target.value)}
          className="rounded-md border border-border bg-bg-card px-3 py-2 text-body text-fg outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
        />
      )}
      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} className="rounded-md px-2.5 py-1 text-caption font-medium text-fg-muted hover:bg-bg-secondary">Cancel</button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-accent-500 px-3 py-1 text-caption font-medium text-bg-card transition hover:bg-accent-600 disabled:opacity-40"
        >
          {saving ? 'Saving...' : 'Save block'}
        </button>
      </div>
    </div>
  )
}
